import { reportError } from '../../services/ErrorReporting';
import storeManager from '../../store/StoreManager';
import utilService from '../../services/UtilService';
import questionReader from '../../store/readHelpers/QuestionReader';

const deliveryModeNames = {
    EXTERNAL_DELIVERY: 'externalDelivery',
    EXCLUSIVE_EXTERNAL_DELIVERY: 'exclusiveExternalDelivery',
    BLANK_DELIVERY: 'blankDelivery',
    WITHDRAW_DELIVERY: 'withdrawDelivery',
};

function getNumberOfDecimals(number) {
    const parts = `${number}`.split('.');
    if (parts.length === 1) {
        return 0;
    }
    return parts[1].length;
}

function getQuestionResult(questionResults, contentRevisionId) {
    return questionResults[contentRevisionId];
}

function questionHasResult(questionResults, contentRevisionId) {
    return getQuestionResult(questionResults, contentRevisionId) !== undefined;
}

export function sumScores(scoreItems) {
    const totals = scoreItems.reduce((accumulator, scoreItem) => {
        const newValues = { ...accumulator };
        const { score, maxScore } = scoreItem;

        newValues.maxDecimals = Math.max(getNumberOfDecimals(score), getNumberOfDecimals(maxScore), newValues.maxDecimals);
        newValues.totalScore += score;

        if (!isNaN(accumulator.maxTotalScore) && !isNaN(maxScore)) {
            newValues.maxTotalScore += maxScore;
        } else {
            newValues.maxTotalScore = undefined;
        }

        return newValues;
    }, { totalScore: 0, maxTotalScore: 0, maxDecimals: 0 });

    totals.totalScore = totals.totalScore.toFixed(totals.maxDecimals);
    if (totals.maxTotalScore !== undefined) {
        totals.maxTotalScore = totals.maxTotalScore.toFixed(totals.maxDecimals);
    }

    return { totalScore: totals.totalScore, maxTotalScore: totals.maxTotalScore };
}

const resultReader = {
    deliveryModeNames,
    getScoreTotalsAsStrings(state = storeManager.getState()) {
        try {
            const { result, assessment } = state;
            const { showScore, isGraded, containsManualScores } = result.metaData;

            if (showScore !== true || !result.questions) {
                return undefined;
            }

            const questions = assessment.sections.reduce((previousArray, section) => {
                return previousArray.concat(section.questions);
            }, []);

            const containsManuallyMarkedQuestionTypes = questions.some(
                question => utilService.isQuestionTypeManuallyScored(question.objectType)
            );

            if (
                (!isGraded && containsManuallyMarkedQuestionTypes)
                || (isGraded && containsManuallyMarkedQuestionTypes && !containsManualScores)
            ) {
                return undefined;
            }

            const hasMissingResults = questions.some((question) => {
                return questionReader.canHaveResult(question)
                    && !questionHasResult(result.questions, question.contentRevisionId);
            });
            if (hasMissingResults) {
                return undefined;
            }

            const scoreItems = questions
                .filter(question => questionReader.canHaveResult(question))
                .map((question) => {
                    const questionResult = getQuestionResult(result.questions, question.contentRevisionId);
                    const scoreToUse = (questionResult.manualScore && questionResult.manualScore.length === 1)
                        ? questionResult.manualScore[0]
                        : questionResult.score;
                    return {
                        maxScore: parseFloat(question.maxScore),
                        score: scoreToUse,
                    };
                });

            return sumScores(scoreItems);
        } catch (error) {
            reportError(error);
        }

        return undefined;
    },
    getDeliveryMode(state = storeManager.getState()) {
        const { result } = state;
        const { deliveryMode } = result.metaData;

        return deliveryMode;
    },
    hasExclusiveExternalDelivery() {
        return this.getDeliveryMode() === deliveryModeNames.EXCLUSIVE_EXTERNAL_DELIVERY;
    },
    getScoreFromResultOrOutcome(outcomeForQuestion, resultForQuestion) {
        const score = resultForQuestion?.manualScore?.length === 1 ? resultForQuestion?.manualScore[0] : resultForQuestion?.score;
        const scoreFromResultState = parseFloat(score);
        const scoreFromOutcome = parseFloat(outcomeForQuestion?.SCORE?.value);
        if (!isNaN(scoreFromResultState)) {
            return scoreFromResultState;
        }
        return isNaN(scoreFromOutcome) ? null : scoreFromOutcome;
    },
};

export default resultReader;
